// Solicitação de Cadastro de Organização (legado: parish)
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  Divider,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  CircularProgress,
  Snackbar,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DomainIcon from '@mui/icons-material/Domain';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../../brands/BrandProvider';
import {
  parishService,
  type PublicRecipientProfilePayload,
} from '../../services/parishService';
import TermsOfService from '../../components/TermsOfService';
import { legalService, PLATFORM_SERVICE_CONTRACT_KEY } from '../../services/legalService';
import { STITCH } from '../../theme/stitchRegistrationTokens';
import {
  ANNUAL_REVENUE_BANDS,
  annualRevenueBandLabel,
  isKnownAnnualRevenueBand,
} from '../../constants/annualRevenueBands';
import { formatCpfCnpjInput, isValidCPF, isValidCNPJ, cleanDocument } from '../../utils/cpfCnpjValidator';
import { phoneValidation, applyPhoneMask } from '../../utils/phoneValidation';
import { formatCepInput, lookupViaCep, onlyDigitsCep } from '../../utils/viaCep';

function maskDdMmYyyy(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

function buildRecipientProfileForRequest(formData: {
  name: string;
  email: string;
  phone: string;
  signerCpf: string;
  parishData: { cnpj: string };
  recipientProfile: {
    companyName: string;
    tradingName: string;
    annualRevenue: string;
    foundingDate: string;
    repBirthdate: string;
    repPhone: string;
    holderName: string;
    bank: string;
    branchNumber: string;
    branchCheckDigit: string;
    accountNumber: string;
    accountCheckDigit: string;
    accountType: 'checking' | 'savings';
  };
}): PublicRecipientProfilePayload {
  const rev = formData.recipientProfile.annualRevenue.replace(/\D/g, '');
  const founding = formData.recipientProfile.foundingDate.trim();
  const repPhoneCombined = formData.recipientProfile.repPhone.trim()
    ? formData.recipientProfile.repPhone.trim()
    : formData.phone.trim();
  const repPhoneMasked = applyPhoneMask(repPhoneCombined);
  const repPhoneFinal = repPhoneMasked || repPhoneCombined;
  const acctDigit = String(formData.recipientProfile.accountCheckDigit || '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, 2);
  const holderDocFormatted = formatCpfCnpjInput(cleanDocument(formData.parishData.cnpj));
  return {
    registerType: 'corporation',
    corporation: {
      companyName: formData.recipientProfile.companyName.trim(),
      tradingName: formData.recipientProfile.tradingName.trim(),
      annualRevenue: rev.length >= 1 ? rev : '500000',
      ...(founding ? { foundingDate: founding } : {}),
      representative: {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        document: cleanDocument(formData.signerCpf),
        birthdate: formData.recipientProfile.repBirthdate.trim(),
        ...(repPhoneFinal ? { phone: repPhoneFinal } : {}),
        selfDeclaredRepresentative: true,
      },
    },
    bankAccount: {
      holderName: formData.recipientProfile.holderName.trim(),
      holderDocument: holderDocFormatted.trim(),
      bank: formData.recipientProfile.bank.replace(/\D/g, '').slice(0, 4),
      branchNumber: formData.recipientProfile.branchNumber.replace(/\D/g, ''),
      branchCheckDigit:
        formData.recipientProfile.branchCheckDigit.replace(/\D/g, '').slice(0, 2) ||
        undefined,
      accountNumber: formData.recipientProfile.accountNumber.replace(/\D/g, ''),
      accountCheckDigit: acctDigit,
      type: formData.recipientProfile.accountType,
    },
  };
}

function RegistrationSectionHeader({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: React.ElementType;
  title: string;
  subtitle?: string;
}) {
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Icon sx={{ fontSize: 26, color: STITCH.primary }} aria-hidden />
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 700,
            color: STITCH.primary,
            letterSpacing: '-0.02em',
            fontSize: { xs: '1.05rem', sm: '1.15rem' },
          }}
        >
          {title}
        </Typography>
      </Stack>
      {subtitle ? (
        <Typography variant="body2" sx={{ mt: 0.75, pl: { xs: 0, sm: 4.25 }, color: STITCH.onSurfaceVariant }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

function StitchStepper({
  steps,
  activeStep,
  segmentBg,
}: {
  steps: string[];
  activeStep: number;
  segmentBg: string;
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        overflowX: 'auto',
        pb: 1,
        mb: { xs: 2, sm: 4 },
        mx: { xs: -0.5, sm: 0 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          minWidth: { xs: 520, sm: '100%' },
          position: 'relative',
          px: 1,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            left: 16,
            right: 16,
            height: '2px',
            bgcolor: STITCH.outlineVariant,
            zIndex: 0,
          }}
        />
        {steps.map((label, i) => {
          const active = i === activeStep;
          const done = i < activeStep;
          return (
            <Box
              key={label}
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                bgcolor: segmentBg,
                px: 1.5,
                flex: '1 1 0',
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  ...(active || done
                    ? {
                        bgcolor: STITCH.primary,
                        color: STITCH.onPrimary,
                        boxShadow: '0 4px 12px rgba(52, 0, 95, 0.22)',
                      }
                    : {
                        bgcolor: STITCH.surfaceContainerHigh,
                        border: `2px solid ${STITCH.outlineVariant}`,
                        color: STITCH.onSurfaceVariant,
                      }),
                }}
              >
                {done ? <CheckIcon sx={{ fontSize: 22 }} /> : i + 1}
              </Box>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: active ? 700 : 600,
                  color: active ? STITCH.primary : STITCH.onSurfaceVariant,
                  textAlign: 'center',
                  lineHeight: 1.25,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function StitchCardHeader({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: React.ElementType;
  title: string;
  subtitle: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 3,
        bgcolor: STITCH.surfaceLow,
        borderBottom: `1px solid ${alpha(STITCH.outlineVariant, 0.35)}`,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '12px',
          bgcolor: STITCH.primaryFixed,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ color: STITCH.onPrimaryFixed, fontSize: 28 }} aria-hidden />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            fontWeight: 600,
            color: STITCH.primary,
            lineHeight: 1.3,
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', color: STITCH.onSurfaceVariant, mt: 0.5, lineHeight: 1.5 }}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
}

function FieldHelp({ title }: { title: string }) {
  return (
    <Tooltip title={title} placement="top" arrow enterTouchDelay={0}>
      <IconButton
        size="small"
        edge="end"
        tabIndex={-1}
        aria-label="Mais informações"
        sx={{
          color: 'action.active',
          p: { xs: 0.85, sm: 0.25 },
          minWidth: { xs: 44, sm: 34 },
          minHeight: { xs: 44, sm: 34 },
        }}
      >
        <HelpOutlineIcon sx={{ fontSize: { xs: 20, sm: 18 } }} />
      </IconButton>
    </Tooltip>
  );
}

const PARISH_REG_STEP_META: { icon: React.ElementType; title: string; subtitle: string }[] = [
  {
    icon: PersonIcon,
    title: 'Informações do Responsável',
    subtitle: 'Preencha os dados do responsável legal pelo registro.',
  },
  {
    icon: DomainIcon,
    title: 'Organização e recebimentos',
    subtitle:
      'Dados institucionais, dados da empresa (PJ), representante legal e conta bancária para repasses.',
  },
  {
    icon: ArticleOutlinedIcon,
    title: 'Termos de uso',
    subtitle: 'Leia o contrato e aceite para seguir com o envio da solicitação.',
  },
  {
    icon: CheckCircleIcon,
    title: 'Confirmação',
    subtitle: 'Revise todas as informações antes de enviar.',
  },
];

const ParishRegistrationRequest: React.FC<{ embedded?: boolean; customPageInviteToken?: string }> = ({
  embedded = false,
  customPageInviteToken,
}) => {
  const navigate = useNavigate();
  const brand = useBrand();
  const isZuadariaSignup = brand.key === 'zuadaria';
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Responsável', 'Organização e recebimentos', 'Termos', 'Confirmação'];
  const segmentBg = embedded ? STITCH.surfaceLowest : STITCH.bg;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    signerCpf: '',
    parishData: {
      name: '',
      address: '',
      streetNumber: '',
      neighborhood: '',
      ibgeMunicipioCode: '',
      city: '',
      state: '',
      zipCode: '',
      parishEmail: '',
      description: '',
      cnpj: ''
    },
    recipientProfile: {
      companyName: '',
      tradingName: '',
      annualRevenue: '',
      foundingDate: '',
      repBirthdate: '',
      repPhone: '',
      holderName: '',
      bank: '',
      branchNumber: '',
      branchCheckDigit: '',
      accountNumber: '',
      accountCheckDigit: '',
      accountType: 'checking' as 'checking' | 'savings'
    }
  });

  /** Preenche razão fantasia titular quando ainda estiver em branco. */
  useEffect(() => {
    const n = formData.parishData.name.trim();
    if (!n) return;
    setFormData((prev) => {
      const rp = prev.recipientProfile;
      let changed = false;
      const next = { ...rp };
      if (!rp.companyName.trim()) {
        next.companyName = n;
        changed = true;
      }
      if (!rp.tradingName.trim()) {
        next.tradingName = n;
        changed = true;
      }
      if (!rp.holderName.trim()) {
        next.holderName = n;
        changed = true;
      }
      if (!changed) return prev;
      return { ...prev, recipientProfile: next };
    });
  }, [formData.parishData.name]);

  const holderDocumentFromParishCnpj = useMemo(() => {
    const d = cleanDocument(formData.parishData.cnpj);
    if (d.length === 14) return formatCpfCnpjInput(d);
    return '';
  }, [formData.parishData.cnpj]);

  const [formErrors, setFormErrors] = useState<any>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState<boolean>(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [showTermsDialog, setShowTermsDialog] = useState<boolean>(false);
  const [contractViewed, setContractViewed] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [legalAccepted, setLegalAccepted] = useState<{ key: string; version: string }[]>([]);
  const [duplicateChecks, setDuplicateChecks] = useState({
    email: false,
    signerCpf: false,
    parishCnpj: false,
    parishName: false
  });
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);

  const fillAddressFromCep = async (rawCep: string) => {
    const digits = onlyDigitsCep(rawCep);
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const found = await lookupViaCep(digits);
      if (!found) {
        setFormErrors((prev: any) => ({
          ...prev,
          parishZipCode: 'CEP não encontrado. Confira e preencha o endereço manualmente.',
        }));
        return;
      }
      setFormData((prev) => ({
        ...prev,
        parishData: {
          ...prev.parishData,
          zipCode: formatCepInput(found.cep),
          address: found.logradouro || prev.parishData.address,
          neighborhood: found.bairro || prev.parishData.neighborhood,
          city: found.localidade || prev.parishData.city,
          state: found.uf || prev.parishData.state,
          ibgeMunicipioCode: found.ibge || prev.parishData.ibgeMunicipioCode,
        },
      }));
      setFormErrors((prev: any) => {
        const next = { ...prev };
        delete next.parishZipCode;
        delete next.parishAddress;
        delete next.parishNeighborhood;
        delete next.parishCity;
        delete next.parishState;
        return next;
      });
    } finally {
      setCepLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target;
    const name = (target.name || '').trim();
    const value = target.value;

    if (name.startsWith('parish')) {
      type ParishKey = keyof (typeof formData)['parishData'];
      const parishKeyMap: Record<string, ParishKey> = {
        parishName: 'name',
        parishAddress: 'address',
        parishStreetNumber: 'streetNumber',
        parishNeighborhood: 'neighborhood',
        parishIbge: 'ibgeMunicipioCode',
        parishCity: 'city',
        parishState: 'state',
        parishZipCode: 'zipCode',
        parishEmail: 'parishEmail',
        parishDescription: 'description',
        parishCnpj: 'cnpj',
      };
      const key = parishKeyMap[name];
      if (!key) return;

      setFormData((prev) => {
        const pd = prev.parishData;
        let nextPd: typeof pd;
        if (key === 'cnpj') {
          const digits = cleanDocument(value).slice(0, 14);
          nextPd = { ...pd, cnpj: formatCpfCnpjInput(digits) };
        } else if (key === 'zipCode') {
          nextPd = { ...pd, zipCode: formatCepInput(value) };
        } else if (key === 'ibgeMunicipioCode') {
          nextPd = { ...pd, ibgeMunicipioCode: value.replace(/\D/g, '').slice(0, 7) };
        } else {
          nextPd = { ...pd, [key]: value };
        }
        return { ...prev, parishData: nextPd };
      });

      if (key === 'name' && value.trim().length >= 3) {
        debouncedCheckDuplicates(undefined, undefined, undefined, value.trim());
      } else if (key === 'name') {
        setDuplicateChecks((prev) => ({ ...prev, parishName: false }));
      }

      if (key === 'cnpj') {
        const digits = cleanDocument(value).slice(0, 14);
        const cnpjVal = formatCpfCnpjInput(digits);
        if (isValidCNPJ(cnpjVal)) {
          debouncedCheckDuplicates(undefined, undefined, cnpjVal, undefined);
        } else {
          setDuplicateChecks((prev) => ({ ...prev, parishCnpj: false }));
        }
      }

      if (key === 'zipCode' && onlyDigitsCep(value).length === 8) {
        void fillAddressFromCep(value);
      }
      return;
    }

    if (name === 'signerCpf') {
      const digits = cleanDocument(value).slice(0, 11);
      const signerOnly = formatCpfCnpjInput(digits);
      setFormData((prev) => ({ ...prev, signerCpf: signerOnly }));
      if (isValidCPF(signerOnly)) {
        debouncedCheckDuplicates(undefined, signerOnly, undefined, undefined);
      } else {
        setDuplicateChecks((prev) => ({ ...prev, signerCpf: false }));
      }
    } else if (name === 'email') {
      setFormData((prev) => ({ ...prev, email: value }));

      if (/\S+@\S+\.\S+/.test(value)) {
        debouncedCheckDuplicates(value, undefined, undefined, undefined);
      } else {
        setDuplicateChecks((prev) => ({ ...prev, email: false }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value } as typeof prev));
    }
  };

  const validateStep = (): boolean => {
    let valid = true;
    const errors: any = {};

    if (activeStep === 0) {
      if (!formData.name) { errors.name = 'Nome é obrigatório'; valid = false; }
      if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) { errors.email = 'Email inválido'; valid = false; }
      if (!formData.phone) { 
        errors.phone = 'Telefone é obrigatório'; 
        valid = false; 
      } else {
        const phoneValidationResult = phoneValidation('Telefone inválido')(formData.phone);
        if (phoneValidationResult !== true) {
          errors.phone = 'Telefone inválido';
          valid = false;
        }
      }
      if (!formData.signerCpf) {
        errors.signerCpf = 'CPF do responsável é obrigatório';
        valid = false;
      } else if (!isValidCPF(formData.signerCpf)) {
        errors.signerCpf = 'Informe um CPF válido (somente números do responsável)';
        valid = false;
      } else if (duplicateChecks.signerCpf) {
        errors.signerCpf = 'Este CPF já está cadastrado como responsável em outra solicitação';
        valid = false;
      }
      
      // Verificar email duplicado
      if (duplicateChecks.email) {
        errors.email = 'Este email já está cadastrado';
        valid = false;
      }
    }

    if (activeStep === 1) {
      if (!formData.parishData.name) {
        errors.parishName = 'Nome da organização é obrigatório';
        valid = false;
      } else if (duplicateChecks.parishName) {
        errors.parishName = 'Já existe uma organização com este nome';
        valid = false;
      }
      if (!formData.parishData.address.trim()) {
        errors.parishAddress = 'Logradouro é obrigatório';
        valid = false;
      }
      if (!formData.parishData.streetNumber.trim()) {
        errors.parishStreetNumber = 'Número é obrigatório';
        valid = false;
      }
      if (!formData.parishData.neighborhood.trim()) {
        errors.parishNeighborhood = 'Bairro é obrigatório';
        valid = false;
      }
      if (onlyDigitsCep(formData.parishData.zipCode).length !== 8) {
        errors.parishZipCode = 'CEP deve ter 8 dígitos';
        valid = false;
      }
      if (!formData.parishData.city) {
        errors.parishCity = 'Cidade obrigatória';
        valid = false;
      }
      if (!formData.parishData.state || formData.parishData.state.length !== 2) {
        errors.parishState = 'UF inválida'; valid = false;
      }
      if (!formData.parishData.cnpj) {
        errors.parishCnpj = 'CNPJ da organização é obrigatório';
        valid = false;
      } else if (!isValidCNPJ(formData.parishData.cnpj)) {
        errors.parishCnpj = 'CNPJ da organização inválido (14 dígitos)';
        valid = false;
      } else if (duplicateChecks.parishCnpj) {
        errors.parishCnpj = 'Já existe uma organização cadastrada com este CNPJ';
        valid = false;
      }

      const rp = formData.recipientProfile;
      const dobRe = /^\d{2}\/\d{2}\/\d{4}$/;
      if (!rp.companyName.trim()) {
        errors.rcpCompanyName = 'Razão social ou nome oficial da organização é obrigatório';
        valid = false;
      }
      if (!rp.tradingName.trim()) {
        errors.rcpTradingName = 'Nome fantasia é obrigatório';
        valid = false;
      }
      if (!isKnownAnnualRevenueBand(rp.annualRevenue)) {
        errors.rcpAnnualRevenue = 'Selecione a faixa de faturamento anual da instituição';
        valid = false;
      }
      if (!rp.repBirthdate.trim() || !dobRe.test(rp.repBirthdate.trim())) {
        errors.rcpRepBirthdate = 'Data de nascimento do representante obrigatória (DD/MM/AAAA)';
        valid = false;
      }
      const repPhoneCombined = rp.repPhone.trim() ? rp.repPhone : formData.phone;
      if (repPhoneCombined) {
        const pr = phoneValidation('Telefone do representante inválido')(repPhoneCombined);
        if (pr !== true) {
          errors.rcpRepPhone = String(pr);
          valid = false;
        }
      }
      const fdOrigin = rp.foundingDate.trim();
      if (fdOrigin && !dobRe.test(fdOrigin)) {
        errors.rcpFoundingDate = 'Data de fundação em DD/MM/AAAA ou deixe em branco';
        valid = false;
      }

      if (!rp.holderName.trim()) {
        errors.rcpHolderName = 'Nome do titular da conta bancária é obrigatório';
        valid = false;
      } else if (rp.holderName.trim().length > 30) {
        errors.rcpHolderName = `Titular da conta: no máximo 30 caracteres (Pagar.me). Atual: ${rp.holderName.trim().length}`;
        valid = false;
      }

      const bankCd = rp.bank.replace(/\D/g, '');
      if (!bankCd || bankCd.length < 3) {
        errors.rcpBank = 'Código do banco (3 dígitos, ex.: 237).';
        valid = false;
      }
      if (!rp.branchNumber.replace(/\D/g, '')) {
        errors.rcpBranchNumber = 'Número da agência obrigatório';
        valid = false;
      }
      if (!rp.accountNumber.replace(/\D/g, '')) {
        errors.rcpAccountNumber = 'Conta obrigatória';
        valid = false;
      }
      const acd = String(rp.accountCheckDigit || '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .trim();
      if (!acd) {
        errors.rcpAccountCheckDigit =
          'Dígito da conta obrigatório (alguns bancos usam X)';
        valid = false;
      }
    }

    if (activeStep === 2) {
      if (!contractViewed) {
        errors.terms = 'Abra e leia o contrato antes de aceitar';
        valid = false;
      }
      if (!termsAccepted) { 
        errors.terms = 'Você deve aceitar os termos de uso para continuar'; 
        valid = false; 
      }
      if (!String(signatureName || formData.name).trim()) {
        errors.signatureName = 'Informe o nome de quem assina';
        valid = false;
      }
    }

    setFormErrors(errors);
    return valid;
  };

  const handleNext = () => {
    // Verificar se há duplicatas antes de avançar
    if (activeStep === 0 && (duplicateChecks.email || duplicateChecks.signerCpf)) {
      setError('Não é possível continuar com e-mail ou CPF do responsável já cadastrados.');
      return;
    }
    
    if (
      activeStep === 1 &&
      (duplicateChecks.parishName || duplicateChecks.parishCnpj)
    ) {
      setError('Não é possível continuar com nome de organização ou CNPJ já cadastrados.');
      return;
    }
    
    if (validateStep()) {
      setError(null);
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleTermsAccept = async () => {
    try {
      const template = await legalService.getTemplate(PLATFORM_SERVICE_CONTRACT_KEY);
      setLegalAccepted([{ key: template.key, version: template.version }]);
    } catch {
      /* backend recusa se a versão faltar */
    }
    setContractViewed(true);
    setTermsAccepted(true);
    if (!signatureName.trim()) setSignatureName(formData.name);
    setShowTermsDialog(false);
  };

  const handleShowTerms = () => {
    setShowTermsDialog(true);
  };


  const checkForDuplicates = async (
    email?: string,
    signerCpf?: string,
    parishCnpj?: string,
    parishName?: string
  ) => {
    if (!email && !signerCpf && !parishCnpj && !parishName) return;

    setCheckingDuplicates(true);
    try {
      const result = await parishService.checkDuplicates(email, signerCpf, parishCnpj, parishName);
      setDuplicateChecks(result);
    } catch (error) {
      console.error('Erro ao verificar duplicatas:', error);
    } finally {
      setCheckingDuplicates(false);
    }
  };

  // Debounce para verificação de duplicatas
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);

  const debouncedCheckDuplicates = (
    email?: string,
    signerCpf?: string,
    parishCnpj?: string,
    parishName?: string
  ) => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const timeout = setTimeout(() => {
      checkForDuplicates(email, signerCpf, parishCnpj, parishName);
    }, 800);

    setDebounceTimeout(timeout);
  };

  const getFieldStatusIcon = (fieldType: 'email' | 'signerCpf' | 'parishCnpj' | 'parishName') => {
    let fieldValue = '';
    let isValid = false;

    switch (fieldType) {
      case 'email':
        fieldValue = formData.email;
        isValid = /\S+@\S+\.\S+/.test(fieldValue);
        break;
      case 'signerCpf':
        fieldValue = formData.signerCpf;
        isValid = isValidCPF(fieldValue);
        break;
      case 'parishCnpj':
        fieldValue = formData.parishData.cnpj;
        isValid = isValidCNPJ(fieldValue);
        break;
      case 'parishName':
        fieldValue = formData.parishData.name;
        isValid = fieldValue.trim().length >= 3;
        break;
      default:
        break;
    }

    if (checkingDuplicates) {
      return <CircularProgress size={20} />;
    }

    if (!fieldValue || !isValid) return null;

    const isDuplicate =
      fieldType === 'signerCpf'
        ? duplicateChecks.signerCpf
        : fieldType === 'parishCnpj'
          ? duplicateChecks.parishCnpj
          : duplicateChecks[fieldType];
    
    if (isDuplicate) {
      return <ErrorIcon color="error" />;
    } else {
      return <CheckCircleIcon color="success" />;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const recipientProfile = buildRecipientProfileForRequest(formData);
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        signerCpf: formData.signerCpf,
        parishData: {
          ...formData.parishData,
          zipCode: onlyDigitsCep(formData.parishData.zipCode),
          ibgeMunicipioCode: formData.parishData.ibgeMunicipioCode.replace(/\D/g, '').slice(0, 7),
        },
        termsAccepted,
        signatureName: signatureName.trim() || formData.name,
        contractViewed,
        legalAccepted,
        recipientProfile,
        ...(customPageInviteToken ? { customPageInviteToken } : {}),
      };
      await parishService.requestParishRegistration(payload);
      setSuccess(true);
      setTimeout(() => navigate('/solicitacao-enviada'), 2000);
    } catch (err: any) {
      const pendencies = err.response?.data?.data?.pendencies as string[] | undefined;
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao enviar solicitação.';
      setError(errorMessage + (pendencies?.length ? ` (${pendencies.length} pendências)` : ''));
      setShowErrorSnackbar(true);
      if (pendencies?.length) {
        setActiveStep(1);
      }

      // Se for erro de email duplicado, destacar o campo de email
      if (errorMessage.includes('email')) {
        setFormErrors((prev: any) => ({ ...prev, email: errorMessage }));
        // Voltar para o passo do email se não estiver nele
        if (activeStep !== 0) {
          setActiveStep(0);
        }
      }
      // Se for erro de CPF/CNPJ duplicado, destacar o campo de CPF/CNPJ
      if (errorMessage.toLowerCase().includes('cpf')) {
        setFormErrors((prev: any) => ({ ...prev, signerCpf: errorMessage }));
        if (activeStep !== 0) setActiveStep(0);
      }
      if (errorMessage.includes('CNPJ')) {
        setFormErrors((prev: any) => ({ ...prev, parishCnpj: errorMessage }));
        if (!errorMessage.includes('responsável') && activeStep !== 1) setActiveStep(1);
      }
      // Se for erro de nome de organização/paróquia duplicado, destacar o campo
      if (
        (errorMessage.includes('paróquia') || errorMessage.includes('organização')) &&
        errorMessage.includes('nome')
      ) {
        setFormErrors((prev: any) => ({ ...prev, parishName: errorMessage }));
        // Voltar para o passo da organização se não estiver nele
        if (activeStep !== 1) {
          setActiveStep(1);
        }
      }
      const low = errorMessage.toLowerCase();
      if (
        low.includes('recebedor') ||
        low.includes('titular') ||
        low.includes('banco') ||
        low.includes('conta') ||
        low.includes('representante') ||
        low.includes('nascimento')
      ) {
        if (activeStep !== 1) setActiveStep(1);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 0 }}>
            <Grid container spacing={{ xs: 2.5, sm: 2.5 }}>
              <Grid item xs={12}>
                <TextField
                  label="Nome completo"
                  name="name"
                  fullWidth
                  required
                  placeholder="Ex: João da Silva"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  inputProps={{ autoComplete: 'name' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="E-mail de login"
                  name="email"
                  type="email"
                  fullWidth
                  required
                  placeholder="nome@exemplo.com"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!formErrors.email}
                  helperText={
                    formErrors.email ||
                    (duplicateChecks.email ? 'Este e-mail já está cadastrado' : undefined)
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ alignItems: 'center', gap: 0.25 }}>
                        <FieldHelp title="Usado para entrar no painel e receber o link para criar a senha. Se a organização tiver outro e-mail institucional, você informa na próxima etapa." />
                        {getFieldStatusIcon('email')}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Telefone"
                  name="phone"
                  fullWidth
                  required
                  placeholder="(11) 98765-4321"
                  value={formData.phone}
                  onChange={(e) => {
                    const formatted = applyPhoneMask(e.target.value);
                    setFormData({ ...formData, phone: formatted });
                  }}
                  error={!!formErrors.phone}
                  helperText={formErrors.phone || 'Celular ou fixo com DDD'}
                  inputProps={{
                    maxLength: 15,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="CPF do responsável"
                  name="signerCpf"
                  fullWidth
                  required
                  placeholder="000.000.000-00"
                  value={formData.signerCpf}
                  onChange={handleChange}
                  error={!!formErrors.signerCpf}
                  helperText={
                    formErrors.signerCpf ||
                    (duplicateChecks.signerCpf ? 'Este CPF já está cadastrado em outra solicitação' : undefined)
                  }
                  inputProps={{ maxLength: 14 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ alignItems: 'center', gap: 0.25 }}>
                        <FieldHelp title="Apenas o CPF de quem assina e administra o cadastro. O CNPJ da organização é na etapa seguinte." />
                        {getFieldStatusIcon('signerCpf')}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 0 }}>
            <Alert
              severity="info"
              variant="outlined"
              icon={false}
              sx={{
                py: 1,
                px: 1.5,
                borderRadius: '8px',
                borderColor: alpha(STITCH.outlineVariant, 0.8),
                bgcolor: alpha(STITCH.primaryFixed, 0.35),
              }}
            >
              <Typography variant="body2">
                Organizamos em blocos: primeiro o cadastro institucional, depois os dados da empresa (PJ), o representante legal e
                por fim a conta onde caem os repasses das inscrições.
              </Typography>
            </Alert>

            <RegistrationSectionHeader
              icon={DomainIcon}
              title="Dados institucionais"
              subtitle="Nome, CNPJ e endereço da organização (empresa, instituição, comunidade etc.). O CNPJ aqui é o mesmo que será usado como documento do titular da conta de recebimentos."
            />
            <Grid container spacing={{ xs: 2.5, sm: 2 }}>
              <Grid item xs={12}>
                <TextField
                  label="Nome da organização"
                  name="parishName"
                  fullWidth
                  required
                  value={formData.parishData.name}
                  onChange={handleChange}
                  error={!!formErrors.parishName}
                  helperText={formErrors.parishName || (duplicateChecks.parishName ? 'Já existe uma organização com este nome' : 'Mínimo 3 caracteres')}
                  inputProps={{ name: 'parishName', id: 'parishName' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {getFieldStatusIcon('parishName')}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="CNPJ da organização"
                  name="parishCnpj"
                  fullWidth
                  required
                  value={formData.parishData.cnpj}
                  onChange={handleChange}
                  error={!!formErrors.parishCnpj}
                  helperText={
                    formErrors.parishCnpj ||
                    (duplicateChecks.parishCnpj ? 'Já existe organização com este CNPJ' : '14 dígitos — você pode digitar só números ou com máscara')
                  }
                  inputProps={{ name: 'parishCnpj', id: 'parishCnpj', maxLength: 18 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{getFieldStatusIcon('parishCnpj')}</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="E-mail institucional"
                  name="parishEmail"
                  fullWidth
                  type="email"
                  value={formData.parishData.parishEmail}
                  onChange={handleChange}
                  placeholder="Opcional"
                  helperText="Em branco, usamos o e-mail de login da etapa anterior"
                  inputProps={{
                    name: 'parishEmail',
                    id: 'parishEmail',
                    autoComplete: 'email',
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <FieldHelp title="E-mail de contato da organização usado em cadastros de recebimento. Se não preencher, repetimos o e-mail de login da etapa anterior." />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="CEP"
                  name="parishZipCode"
                  fullWidth
                  required
                  value={formData.parishData.zipCode}
                  onChange={handleChange}
                  onBlur={() => void fillAddressFromCep(formData.parishData.zipCode)}
                  placeholder="00000-000"
                  error={!!formErrors.parishZipCode}
                  helperText={formErrors.parishZipCode || (cepLoading ? 'Buscando CEP…' : 'Preenche logradouro, bairro, cidade e IBGE')}
                  inputProps={{
                    name: 'parishZipCode',
                    id: 'parishZipCode',
                    autoComplete: 'postal-code',
                    inputMode: 'numeric',
                  }}
                  InputProps={{
                    endAdornment: cepLoading ? (
                      <InputAdornment position="end">
                        <CircularProgress size={18} />
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Logradouro"
                  name="parishAddress"
                  fullWidth
                  required
                  value={formData.parishData.address}
                  onChange={handleChange}
                  error={!!formErrors.parishAddress}
                  helperText={formErrors.parishAddress}
                  inputProps={{ name: 'parishAddress', id: 'parishAddress' }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Número"
                  name="parishStreetNumber"
                  fullWidth
                  required
                  value={formData.parishData.streetNumber}
                  onChange={handleChange}
                  error={!!formErrors.parishStreetNumber}
                  helperText={formErrors.parishStreetNumber}
                  placeholder="Ex.: 100 ou S/N"
                  inputProps={{ name: 'parishStreetNumber', id: 'parishStreetNumber' }}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Bairro"
                  name="parishNeighborhood"
                  fullWidth
                  required
                  value={formData.parishData.neighborhood}
                  onChange={handleChange}
                  error={!!formErrors.parishNeighborhood}
                  helperText={formErrors.parishNeighborhood}
                  inputProps={{ name: 'parishNeighborhood', id: 'parishNeighborhood' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Cidade"
                  name="parishCity"
                  fullWidth
                  required
                  value={formData.parishData.city}
                  onChange={handleChange}
                  error={!!formErrors.parishCity}
                  helperText={formErrors.parishCity}
                  inputProps={{ name: 'parishCity', id: 'parishCity' }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Estado (UF)"
                  name="parishState"
                  fullWidth
                  required
                  inputProps={{ name: 'parishState', id: 'parishState', maxLength: 2 }}
                  value={formData.parishData.state}
                  onChange={handleChange}
                  error={!!formErrors.parishState}
                  helperText={formErrors.parishState}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="IBGE município"
                  name="parishIbge"
                  fullWidth
                  value={formData.parishData.ibgeMunicipioCode}
                  onChange={handleChange}
                  helperText="Preenchido pelo CEP"
                  inputProps={{ name: 'parishIbge', id: 'parishIbge', inputMode: 'numeric' }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Descrição"
                  name="parishDescription"
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Breve histórico ou observações (opcional)"
                  value={formData.parishData.description}
                  onChange={handleChange}
                  inputProps={{ name: 'parishDescription', id: 'parishDescription' }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: { xs: 3, sm: 4 } }} />

            <RegistrationSectionHeader
              icon={BusinessIcon}
              title="Dados da empresa (PJ)"
              subtitle="Razão social e nome fantasia costumam coincidir com o CNPJ informado acima. Valores em branco podem ser preenchidos automaticamente com o nome da organização."
            />
            <Grid container spacing={{ xs: 2.5, sm: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Razão social"
                required
                fullWidth
                value={formData.recipientProfile.companyName}
                error={!!formErrors.rcpCompanyName}
                helperText={formErrors.rcpCompanyName}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    recipientProfile: { ...p.recipientProfile, companyName: e.target.value },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nome fantasia"
                required
                fullWidth
                value={formData.recipientProfile.tradingName}
                error={!!formErrors.rcpTradingName}
                helperText={formErrors.rcpTradingName}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    recipientProfile: { ...p.recipientProfile, tradingName: e.target.value },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!formErrors.rcpAnnualRevenue}>
                <InputLabel id="parish-reg-annual-revenue-label">Faturamento anual estimado</InputLabel>
                <Select
                  labelId="parish-reg-annual-revenue-label"
                  label="Faturamento anual estimado"
                  value={formData.recipientProfile.annualRevenue}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      recipientProfile: { ...p.recipientProfile, annualRevenue: String(e.target.value) },
                    }))
                  }
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Selecione uma faixa</em>
                  </MenuItem>
                  {ANNUAL_REVENUE_BANDS.map((b) => (
                    <MenuItem key={b.valueCentavos} value={b.valueCentavos}>
                      {b.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {formErrors.rcpAnnualRevenue ||
                    'Estimativa anual em reais; usamos o valor da faixa no cadastro da empresa (PJ).'}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Data de fundação (opcional)"
                fullWidth
                placeholder="DD/MM/AAAA"
                value={formData.recipientProfile.foundingDate}
                error={!!formErrors.rcpFoundingDate}
                helperText={formErrors.rcpFoundingDate}
                inputProps={{ maxLength: 10 }}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    recipientProfile: {
                      ...p.recipientProfile,
                      foundingDate: maskDdMmYyyy(e.target.value),
                    },
                  }))
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: { xs: 1, sm: 2 } }} />
            </Grid>
            <Grid item xs={12}>
              <RegistrationSectionHeader
                icon={PersonIcon}
                title="Representante legal"
                subtitle="Quem assina na etapa «Responsável». Falta só a data de nascimento para completar o cadastro da empresa (PJ)."
              />
              <Chip
                size="small"
                label={
                  formData.name.trim() && formData.signerCpf
                    ? `${formData.name.trim()} · ${formData.signerCpf}`
                    : 'Preencha a etapa Responsável antes'
                }
                variant="outlined"
                sx={{
                  mt: 1,
                  maxWidth: '100%',
                  height: 'auto',
                  py: 0.5,
                  '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nascimento do representante"
                required
                fullWidth
                placeholder="DD/MM/AAAA"
                value={formData.recipientProfile.repBirthdate}
                inputProps={{ maxLength: 10 }}
                error={!!formErrors.rcpRepBirthdate}
                helperText={formErrors.rcpRepBirthdate}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    recipientProfile: {
                      ...p.recipientProfile,
                      repBirthdate: maskDdMmYyyy(e.target.value),
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Telefone do representante"
                fullWidth
                placeholder="Opcional"
                value={formData.recipientProfile.repPhone}
                error={!!formErrors.rcpRepPhone}
                helperText={formErrors.rcpRepPhone || 'Em branco, usa o telefone da etapa 1'}
                inputProps={{ maxLength: 15 }}
                onChange={(e) => {
                  const formatted = applyPhoneMask(e.target.value);
                  setFormData((p) => ({
                    ...p,
                    recipientProfile: { ...p.recipientProfile, repPhone: formatted },
                  }));
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: { xs: 1, sm: 2 } }} />
            </Grid>
            <Grid item xs={12}>
              <RegistrationSectionHeader
                icon={AccountBalanceIcon}
                title="Conta para recebimentos"
                subtitle="Dados bancários dos repasses. O CNPJ do titular é preenchido automaticamente com o mesmo CNPJ da organização."
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label="Titular da conta"
                required
                fullWidth
                value={formData.recipientProfile.holderName}
                error={!!formErrors.rcpHolderName}
                helperText={
                  formErrors.rcpHolderName ||
                  `Máximo 30 caracteres (exigência Pagar.me). ${formData.recipientProfile.holderName.length}/30`
                }
                inputProps={{ maxLength: 30 }}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    recipientProfile: {
                      ...p.recipientProfile,
                      holderName: e.target.value.slice(0, 30),
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="CNPJ do titular (conta)"
                required
                fullWidth
                value={holderDocumentFromParishCnpj}
                InputProps={{ readOnly: true }}
                error={!!formErrors.parishCnpj}
                helperText={
                  formErrors.parishCnpj ||
                  (holderDocumentFromParishCnpj
                    ? 'Igual ao CNPJ da organização acima.'
                    : 'Preencha e valide o CNPJ da organização no bloco anterior.')
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Banco"
                required
                fullWidth
                placeholder="237"
                value={formData.recipientProfile.bank}
                error={!!formErrors.rcpBank}
                helperText={formErrors.rcpBank}
                inputProps={{ maxLength: 4 }}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setFormData((p) => ({
                    ...p,
                    recipientProfile: { ...p.recipientProfile, bank: v },
                  }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Agência"
                required
                fullWidth
                value={formData.recipientProfile.branchNumber}
                error={!!formErrors.rcpBranchNumber}
                helperText={formErrors.rcpBranchNumber}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    recipientProfile: {
                      ...p.recipientProfile,
                      branchNumber: e.target.value.replace(/\D/g, '').slice(0, 8),
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Díg. agência"
                fullWidth
                value={formData.recipientProfile.branchCheckDigit}
                helperText="Opcional"
                inputProps={{ maxLength: 2 }}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    recipientProfile: {
                      ...p.recipientProfile,
                      branchCheckDigit: e.target.value.replace(/\D/g, '').slice(0, 2),
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Tipo conta</InputLabel>
                <Select
                  label="Tipo conta"
                  value={formData.recipientProfile.accountType}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      recipientProfile: {
                        ...p.recipientProfile,
                        accountType: e.target.value as 'checking' | 'savings',
                      },
                    }))
                  }
                >
                  <MenuItem value="checking">Conta corrente</MenuItem>
                  <MenuItem value="savings">Poupança</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                label="Conta (sem dígito)"
                required
                fullWidth
                value={formData.recipientProfile.accountNumber}
                error={!!formErrors.rcpAccountNumber}
                helperText={formErrors.rcpAccountNumber}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    recipientProfile: {
                      ...p.recipientProfile,
                      accountNumber: e.target.value.replace(/\D/g, '').slice(0, 13),
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                label="Dígito da conta"
                required
                fullWidth
                placeholder="Ex.: 9 ou X"
                value={formData.recipientProfile.accountCheckDigit}
                error={!!formErrors.rcpAccountCheckDigit}
                helperText={formErrors.rcpAccountCheckDigit}
                inputProps={{ maxLength: 2 }}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    recipientProfile: {
                      ...p.recipientProfile,
                      accountCheckDigit: String(e.target.value)
                        .replace(/[^a-zA-Z0-9]/gi, '')
                        .toUpperCase()
                        .slice(0, 2),
                    },
                  }))
                }
              />
            </Grid>
            </Grid>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 0 }}>
            <Button
              variant="outlined"
              onClick={handleShowTerms}
              sx={{ mt: 0, borderRadius: '8px', borderColor: alpha(STITCH.primary, 0.45), color: STITCH.primary }}
            >
              Ler contrato
            </Button>

            <TextField
              fullWidth
              label="Nome de quem assina"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              error={!!formErrors.signatureName}
              helperText={formErrors.signatureName || 'Nome completo do responsável legal'}
              sx={{ mt: 3 }}
            />

            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => {
                      if (!contractViewed) {
                        setFormErrors((prev: any) => ({ ...prev, terms: 'Abra e leia o contrato antes de aceitar' }));
                        return;
                      }
                      setTermsAccepted(e.target.checked);
                    }}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    Li e aceito o contrato de prestação de serviços (ACUTIS TECNOLOGIA LTDA)
                  </Typography>
                }
              />
              {formErrors.terms && (
                <Typography color="error" variant="caption" display="block">
                  {formErrors.terms}
                </Typography>
              )}
            </Box>
          </Box>
        );
      case 3:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Responsável
            </Typography>
            <Typography>Nome: {formData.name}</Typography>
            <Typography>E-mail de login: {formData.email}</Typography>
            <Typography>Telefone: {formData.phone}</Typography>
            <Typography>CPF: {formData.signerCpf}</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
              Organização
            </Typography>
            <Typography>Nome: {formData.parishData.name}</Typography>
            <Typography>CNPJ: {formData.parishData.cnpj}</Typography>
            <Typography>Localização: {formData.parishData.city} - {formData.parishData.state}</Typography>
            {formData.parishData.address ? (
              <Typography>
                Endereço: {formData.parishData.address}
                {formData.parishData.streetNumber ? `, ${formData.parishData.streetNumber}` : ''}
                {formData.parishData.neighborhood ? ` — ${formData.parishData.neighborhood}` : ''}
                {formData.parishData.zipCode ? ` — CEP ${formData.parishData.zipCode}` : ''}
              </Typography>
            ) : null}
            {(formData.parishData.parishEmail || '').trim() ? (
              <Typography>E-mail institucional: {formData.parishData.parishEmail}</Typography>
            ) : (
              <Typography color="text.secondary">E-mail institucional: mesmo do login</Typography>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
              Recebimentos
            </Typography>
            <Typography>Razão social: {formData.recipientProfile.companyName}</Typography>
            <Typography>Nome fantasia: {formData.recipientProfile.tradingName}</Typography>
            <Typography>
              Faturamento anual (faixa): {annualRevenueBandLabel(formData.recipientProfile.annualRevenue)}
            </Typography>
            <Typography>Representante (nasc.): {formData.recipientProfile.repBirthdate}</Typography>
            <Typography>Titular da conta: {formData.recipientProfile.holderName}</Typography>
            <Typography>CNPJ do titular (conta): {holderDocumentFromParishCnpj || formData.parishData.cnpj}</Typography>
            <Typography>Banco/agência/conta: {formData.recipientProfile.bank}</Typography>
            <Typography sx={{ ml: 2 }}>
              • Ag. {formData.recipientProfile.branchNumber}
              {formData.recipientProfile.branchCheckDigit ? `-${formData.recipientProfile.branchCheckDigit}` : ''} — Conta{' '}
              {formData.recipientProfile.accountNumber}-{formData.recipientProfile.accountCheckDigit} ({formData.recipientProfile.accountType === 'savings' ? 'poupança' : 'corrente'})
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
              Termos
            </Typography>
            <Typography color="success.main">✓ Termos de uso aceitos</Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  const meta = PARISH_REG_STEP_META[activeStep] ?? PARISH_REG_STEP_META[0];

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: embedded ? '100%' : STITCH.maxContentWidth,
        mx: 'auto',
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      }}
    >
      {isZuadariaSignup ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Cadastro no Zuadaria. Sua casa e os shows ficam neste site — não entram no Meu Sacramento.
        </Alert>
      ) : null}
      {isNarrow ? (
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Typography variant="overline" color="text.secondary" display="block" sx={{ letterSpacing: 0.5 }}>
            Etapa {activeStep + 1} de {steps.length}
          </Typography>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 0.5, color: STITCH.primary }}>
            {steps[activeStep]}
          </Typography>
          <Box
            sx={{
              mt: 1.5,
              height: 4,
              borderRadius: 2,
              bgcolor: 'action.hover',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${((activeStep + 1) / steps.length) * 100}%`,
                bgcolor: STITCH.primary,
                transition: 'width 0.2s ease-out',
              }}
            />
          </Box>
        </Box>
      ) : (
        <StitchStepper steps={steps} activeStep={activeStep} segmentBg={segmentBg} />
      )}

      <Paper
        elevation={0}
        sx={{
          overflow: 'hidden',
          borderRadius: '16px',
          bgcolor: STITCH.surfaceLowest,
          boxShadow: STITCH.shadowCard,
          border: `1px solid ${alpha(STITCH.outlineVariant, 0.4)}`,
          mt: embedded ? 0 : { xs: 0, sm: 0 },
          mb: embedded ? 2 : { xs: 2, sm: 3 },
          '& .MuiInputBase-input': { fontSize: '1rem' },
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '& fieldset': { borderColor: STITCH.outlineVariant },
            '&:hover fieldset': { borderColor: STITCH.outlineVariant },
            '&.Mui-focused fieldset': { borderColor: STITCH.primary, borderWidth: '1px' },
          },
        }}
      >
        <StitchCardHeader icon={meta.icon} title={meta.title} subtitle={meta.subtitle} />

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert
              severity="error"
              sx={{
                fontSize: '1rem',
                '& .MuiAlert-message': { fontWeight: 500 },
              }}
              onClose={() => setError(null)}
            >
              <Typography variant="body1" component="div" sx={{ fontWeight: 500 }}>
                ⚠️ {error}
              </Typography>
            </Alert>
          )}
          {success && (
            <Alert severity="success">
              {isZuadariaSignup
                ? 'Cadastro concluído. Verifique o e-mail para definir a senha e entrar no Zuadaria.'
                : 'Cadastro concluído. Verifique o e-mail para definir a senha.'}
            </Alert>
          )}

          {renderStepContent()}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1.5,
            p: 3,
            bgcolor: alpha(STITCH.surfaceLow, 0.92),
            borderTop: `1px solid ${alpha(STITCH.outlineVariant, 0.35)}`,
          }}
        >
          <Button
            variant={isNarrow ? 'outlined' : 'text'}
            color="primary"
            disabled={activeStep === 0 || isSubmitting}
            onClick={handleBack}
            fullWidth={isNarrow}
            size={isNarrow ? 'large' : 'medium'}
            startIcon={<ArrowBackIcon />}
            sx={{
              fontWeight: 600,
              borderRadius: '8px',
              px: 2,
              ...(isNarrow
                ? { borderColor: alpha(STITCH.primary, 0.35) }
                : {
                    color: STITCH.primary,
                    '&:hover': { bgcolor: alpha(STITCH.primary, 0.06) },
                  }),
            }}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            onClick={activeStep === 3 ? handleSubmit : handleNext}
            disabled={isSubmitting}
            fullWidth={isNarrow}
            size={isNarrow ? 'large' : 'medium'}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
            endIcon={!isSubmitting && activeStep < 3 ? <ArrowForwardIcon /> : undefined}
            sx={{
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
              py: 1.25,
              bgcolor: STITCH.primary,
              boxShadow: '0 1px 3px rgba(52,0,95,0.2)',
              '&:hover': { bgcolor: '#4b1e78' },
            }}
          >
            {activeStep === 3 ? (isSubmitting ? 'Enviando...' : 'Enviar solicitação') : 'Próximo'}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: { xs: 10, sm: 9 } }}
      >
        <Alert
          onClose={() => setShowErrorSnackbar(false)}
          severity="error"
          sx={{
            width: '100%',
            fontSize: '1rem',
            fontWeight: 500,
            '& .MuiAlert-message': { fontWeight: 500 },
          }}
        >
          <Typography variant="body1" component="div" sx={{ fontWeight: 600 }}>
            ⚠️ Atenção: {error}
          </Typography>
        </Alert>
      </Snackbar>

      <TermsOfService
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
        onAccept={handleTermsAccept}
        parishName={formData.parishData.name}
        parishAddress={formData.parishData.address}
        parishCity={formData.parishData.city}
        parishState={formData.parishData.state}
        responsibleName={formData.name}
        responsibleEmail={formData.email}
        responsibleCPF={formData.signerCpf}
        parishCNPJ={formData.parishData.cnpj}
      />
    </Box>
  );
};

export default ParishRegistrationRequest;
